import invariant from 'invariant';
import { batch } from 'react-redux';
import { Dispatch } from 'redux';

import { extractKeywordsFromQuery } from 'algolia-api/search/extractKeywordsFromQuery';
import {
  initialFacetFiltersForMainSearch,
  sideFacetsForType,
} from 'algolia-api/search/searchFacets';
import { isLocationEmpty } from 'algolia-api/search/sidefilters';
import { DynamicEndpoint } from 'algolia-api/types/DynamicEndpoint';
import { ListingType } from 'algolia-api/types/ListingType';
import { SearchFilters } from 'algolia-api/types/SearchFilters';
import { SearchFiltersWithType } from 'algolia-api/types/SearchFiltersWithType';
import { SearchLocation } from 'algolia-api/types/SearchLocation';
import { SalariesJobSynonym } from 'modules/salaries/types/SalariesJobSynonym';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { EMPTY_SEARCH_LOCATION, getDefaultSearchRadius } from 'utils/constants';
import { REMOTE_KEYWORDS } from 'utils/constants/search';
import {
  arrToObject,
  filterObject,
  isDefined,
  updateObject,
} from 'utils/functional';
import { getJson } from 'utils/http';
import { setItem } from 'utils/localStorage';
import { MAIN_SEARCH_FACETS } from 'utils/mainSearchFacets';
import { camelCaseToSnakeCase, truncateToUTF8Bytes } from 'utils/string';

import {
  GradSortType,
  MainSearchResults,
  MainStoreMainSearchState,
  SortType,
} from './MainStoreMainSearchState';
import { executeSearchQuery } from './helpers/executeSearchQuery';
import {
  MainStoreSearchRadiusState,
  setSearchRadius as setSearchRadiusBase,
} from './searchRadius';

// Consts
const ENDPOINTS: DynamicEndpoint[] = [
  {
    facetUrl: (synonymId: string) =>
      `/data/salary-surveys/job-synonyms/${synonymId}`,
    facetKey: 'jobFamilyFacet',
    facetName: 'synonymId',
    filterKey: 'compdb.familyId',
    // TODO: Fix this the next time the file is edited.
    // @ts-expect-error fix types
    responseToOptions: ({
      familyId,
      jobSynonym,
    }: {
      familyId: string;
      jobSynonym: SalariesJobSynonym;
    }) => [{ text: jobSynonym.name, id: familyId }],
  },
];
const DEFAULT_TYPE_FACET = 'JOB';
const initialFacetsThatArentLoaded = Object.keys(
  filterObject(MAIN_SEARCH_FACETS, (_, f) => Boolean(f.lazy)),
);
const mainSearchTypes: ListingType[] = [
  'JOB',
  'INTERNSHIP',
  'VOLOP',
  'ORG',
  'GRAD_PROGRAM',
  'ALL',
];
const mainIndexTypes = ['JOB', 'INTERNSHIP', 'VOLOP', 'ORG', 'ALL'];

// Types

type InitializeAction = {
  type: 'SEARCH_INITIALIZE';
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryParams: Record<string, any>;
  location?: SearchLocation;
  defaultSearchType?: string;
};

type InitializeLandingPageAction = {
  type: 'SEARCH_INITIALIZE_LANDING_PAGE';
  landingPagePath: string | null | undefined;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: Record<string, any> | null | undefined;
  autosaveSearch?: boolean;
};

type ResetPageAction = {
  type: 'SEARCH_RESET_PAGE';
};

type SetQueryAction = {
  type: 'SEARCH_SET_QUERY';
  query: string;
};

type SetQueryKeywordsAction = {
  type: 'SET_QUERY_KEYWORDS';
  keywords: Array<string>;
};

type SetSortAction = {
  type: 'SEARCH_SET_SORT';
  sortParam: SortType | GradSortType;
};

type SetPageIndexAction = {
  type: 'SEARCH_SET_PAGE_INDEX';
  pageIndex: number;
};

type ResetFiltersAction = {
  type: 'SEARCH_RESET_FILTERS';
};

type SearchInitiatedByUserAction = {
  type: 'SEARCH_INITIATED_BY_USER';
  searchMode: boolean;
};

type SetFilterAction = {
  type: 'SEARCH_SET_FILTER';
  key: string;
  value: unknown;
};

type SetFiltersAction = {
  type: 'SEARCH_SET_FILTERS';
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filters: Record<string, any> | null | undefined;
};

type SelectFilterOptionAction = {
  type: 'SEARCH_SELECT_FILTER_OPTION';
  key: string;
  option: string | null | undefined;
};

type DeselectFilterOptionAction = {
  type: 'SEARCH_DESELECT_FILTER_OPTION';
  key: string;
  option: string | null | undefined;
};

type SelectFilterSpecificOptionsAction = {
  type: 'SEARCH_SELECT_FILTER_SPECIFIC_OPTIONS';
  key: string;
  options: Array<string>;
};

type SelectAllFilterOptionsAction = {
  type: 'SEARCH_SELECT_ALL_FILTER_OPTIONS';
  key: string;
};

type SetSearchFacetOptionsAction = {
  type: 'SET_SEARCH_FACET_OPTIONS';
  options: Array<string>;
  optionTitles: Record<string, string>;
  facetKey: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  optionRankings: Record<string, any>;
};

type ClearResultsAction = {
  type: 'SEARCH_CLEAR_RESULTS';
};

type SearchStartedAction = {
  type: 'SEARCH_STARTED';
};

type SuccessAction = {
  type: 'SEARCH_RESULTS_SUCCESS';
  results: MainSearchResults;
};

type FetchPageSuccessAction = {
  type: 'SEARCH_FETCH_PAGE_SUCCESS';
  results: MainSearchResults;
};

type SearchAutosaveDisableAction = {
  type: 'SEARCH_AUTOSAVE_DISABLE';
};

type UninitializeSearchAction = {
  type: 'SEARCH_UNINITIALIZE';
};

type ClearLandingSearchLocationAction = {
  type: 'SEARCH_CLEAR_LANDING_SEARCH_LOCATION';
};

export type UpdateSearchLocationAction = {
  type: 'UPDATE_SEARCH_LOCATION';
  location: SearchLocation;
};

type ClearSearchLocationAction = {
  type: 'CLEAR_SEARCH_LOCATION';
};

export type MainStoreMainSearchAction =
  | InitializeAction
  | InitializeLandingPageAction
  | ResetPageAction
  | SetQueryAction
  | SetQueryKeywordsAction
  | SetSortAction
  | SetPageIndexAction
  | ResetFiltersAction
  | SetFilterAction
  | SetFiltersAction
  | SelectFilterOptionAction
  | DeselectFilterOptionAction
  | SelectFilterSpecificOptionsAction
  | SelectAllFilterOptionsAction
  | SetSearchFacetOptionsAction
  | ClearResultsAction
  | SearchInitiatedByUserAction
  | SearchStartedAction
  | SuccessAction
  | FetchPageSuccessAction
  | SearchAutosaveDisableAction
  | UninitializeSearchAction
  | ClearLandingSearchLocationAction
  | UpdateSearchLocationAction
  | ClearSearchLocationAction;

// Reducer

const initialState: MainStoreMainSearchState = {
  query: '',
  pageIndex: 0,
  lastLoadedPageIndex: 0,
  searchId: 0,
  // Incrementing id
  resultsByPage: {},
  initialized: false,
  keywords: [],
  landingPageInitialized: false,
  initialSearchLocationSet: false,
  initialSearchStarted: false,
  initialResultsLoaded: false,
  isSearching: false,
  searchMode: false,
  sort: updateObject(
    {
      GRAD_PROGRAM: 'location-none',
    },
    arrToObject(mainIndexTypes.map((t) => [t, 'relevance'])),
  ),
  // Dont render search results that filter based on these facets until this is
  // loaded
  facetsThatArentLoaded: initialFacetsThatArentLoaded,
  filters: { type: 'JOB' },
  landingPagePath: null,
  queryID: null,
  dynamicFacets: {},
};

export function mainSearchReducer(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: MainStoreMainSearchState = initialState,
  action: MainStoreMainSearchAction,
): MainStoreMainSearchState {
  switch (action.type) {
    case 'SEARCH_INITIALIZE': {
      const { page, q, sort, type } = action.queryParams;
      // does the search term contain something like "remote", "wfh" etc
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      const isRemoteQuery = Boolean(state.keywords?.length > 0);
      const query = Array.isArray(q) ? q[0] : q;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-destructuring
      const defaultSearchType = action.defaultSearchType;
      const filters = initialFacetFiltersForMainSearch(
        // This should be handled in the searchFacet reducer
        // If there is new search facets we need to update initialSearchFacets
        MAIN_SEARCH_FACETS,
        state.facetsThatArentLoaded.length > 0
          ? state.facetsThatArentLoaded
          : Object.keys(state.dynamicFacets),
        {
          ...state.filters,
          ...action.queryParams,
          type: type || defaultSearchType || DEFAULT_TYPE_FACET,
        },
        isRemoteQuery,
      );
      return {
        ...state,
        initialized: true,
        query: isDefined(query)
          ? truncateToUTF8Bytes(query || '', 512)
          : state.query,
        pageIndex: page ? Number(page) - 1 : state.pageIndex,
        sort: sort
          ? updateObject(state.sort, {
              [type]: sort,
            })
          : state.sort,
        filters,
        facetsThatArentLoaded: state.facetsThatArentLoaded,
      };
    }

    case 'SEARCH_INITIALIZE_LANDING_PAGE': {
      const filters = initialFacetFiltersForMainSearch(
        // This should be handled in the searchFacet reducer
        // If there is new search facets we need to update initialSearchFacets
        MAIN_SEARCH_FACETS,
        state.facetsThatArentLoaded,
        {
          ...action.filters,
          // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
          type: (action.filters && action.filters.type) || DEFAULT_TYPE_FACET,
        },
      );

      return {
        ...state,
        initialized: true,
        landingPageInitialized: true,
        landingPagePath: action.landingPagePath,
        filters,
        autosaveSearch: action.autosaveSearch,
      };
    }

    case 'SEARCH_RESET_PAGE': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        landingPagePath: null,
      };
    }

    case 'SEARCH_SET_QUERY': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        landingPagePath: null,
        query: action.query,
        searchMode: true,
      };
    }

    case 'SET_QUERY_KEYWORDS': {
      return { ...state, keywords: action.keywords };
    }

    case 'SEARCH_SET_SORT': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        sort: updateObject(state.sort, {
          [state.filters.type as string]: action.sortParam,
        }),
        searchMode: true,
      };
    }

    case 'SEARCH_SET_PAGE_INDEX': {
      return { ...state, pageIndex: action.pageIndex, searchMode: true };
    }

    case 'SEARCH_SELECT_FILTER_OPTION': {
      const facet = action.key;
      const filter = state.filters[facet];
      const selectedOptions = [
        ...((Array.isArray(filter) && filter) || []),
        action.option,
      ];
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        landingPagePath: null,
        // TODO: Fix this the next time the file is edited.
        // @ts-expect-error fix types
        filters: { ...state.filters, [facet]: selectedOptions },
      };
    }

    case 'SEARCH_SELECT_FILTER_SPECIFIC_OPTIONS': {
      const facet = action.key;
      const selectedOptions = action.options;
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        landingPagePath: null,
        filters: { ...state.filters, [facet]: selectedOptions },
      };
    }

    case 'SEARCH_RESET_FILTERS': {
      const filters = initialFacetFiltersForMainSearch(
        MAIN_SEARCH_FACETS,
        state.facetsThatArentLoaded,
        {
          type: state.filters.type,
        },
        false,
        true,
        /* reset */
      );
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        landingPagePath: null,
        filters,
      };
    }

    case 'SEARCH_SET_FILTER': {
      const facet = action.key;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-destructuring
      const value = action.value;

      const filters = (() => {
        if (facet === 'type' && isDefined(value)) {
          const t = mainSearchTypes.find((i) => i === value);
          invariant(t, 'type must be within main search types');
          return initialFacetFiltersForMainSearch(
            MAIN_SEARCH_FACETS,
            state.facetsThatArentLoaded,
            {
              type: t,
            },
          );
        }

        return updateObject(state.filters, {
          [facet as string]: value,
        });
      })();

      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        landingPagePath: null,
        filters,
      };
    }

    case 'SEARCH_SET_FILTERS': {
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.filters,
        },
      };
    }

    case 'SEARCH_DESELECT_FILTER_OPTION': {
      const facet = action.key;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-destructuring
      const option = action.option;
      invariant(Array.isArray(state.filters[facet]), 'Missing search filter');
      // TODO: Fix this the next time the file is edited.
      // @ts-expect-error fix types
      const selectedOptions = state.filters[facet].filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (v: any) => v !== option,
      );
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        landingPagePath: null,
        filters: { ...state.filters, [facet]: selectedOptions },
      };
    }

    case 'SEARCH_SELECT_ALL_FILTER_OPTIONS': {
      const facet = action.key;
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        landingPagePath: null,
        filters: {
          ...state.filters,
          [facet]: MAIN_SEARCH_FACETS[facet].options,
        },
      };
    }

    case 'SET_SEARCH_FACET_OPTIONS': {
      const { options, optionTitles, optionRankings, facetKey } = action;
      const facet = MAIN_SEARCH_FACETS[facetKey];
      const facetsThatArentLoaded: Array<string> =
        state.facetsThatArentLoaded.filter((f) => f !== facetKey);
      const sideFacets = sideFacetsForType(
        MAIN_SEARCH_FACETS,
        state.filters.type,
      );
      const hasFacet = sideFacets.find((f) => f.id === facet.id);
      const filterValue = facet.id && state.filters[facet.id];
      const updatedFilters =
        facet.id && hasFacet
          ? { ...state.filters, [facet.id]: filterValue || options }
          : state.filters;
      return {
        ...state,
        // This needs to get bumped if we change filter values
        // But if we dont, then it doesnt need to get changed
        searchId:
          filterValue && filterValue !== options
            ? state.searchId + 1
            : state.searchId,
        facetsThatArentLoaded,
        dynamicFacets: {
          ...state.dynamicFacets,
          [facetKey]: {
            ...facet,
            options,
            optionTitles,
            optionRankings,
          },
        },
        // Only set the filter value if the current filter.type supports this
        // facet
        filters: updatedFilters,
      };
    }

    case 'SEARCH_INITIATED_BY_USER': {
      return { ...state, searchMode: action.searchMode };
    }

    case 'SEARCH_STARTED': {
      return {
        ...state,
        initialSearchStarted: true,
        isSearching: true,
        queryID: null,
      };
    }

    case 'SEARCH_RESULTS_SUCCESS': {
      // If the results are from an older search, ignore them
      const lastResults =
        state.resultsByPage[state.lastLoadedPageIndex.toString()];

      if (lastResults && lastResults.timestamp > action.results.timestamp) {
        return state;
      }

      const newSearch = action.results.searchId !== state.resultsForSearchId;
      return {
        ...state,
        queryID: action.results.queryID,
        resultsByPage: newSearch
          ? {
              [action.results.page]: action.results,
            }
          : { ...state.resultsByPage, [action.results.page]: action.results },
        lastLoadedPageIndex: action.results.page,
        resultsForSearchId: action.results.searchId,
        initialResultsLoaded: true,
        isSearching: false,
      };
    }

    case 'SEARCH_FETCH_PAGE_SUCCESS': {
      return {
        ...state,
        queryID: action.results.queryID,
        resultsByPage: {
          ...state.resultsByPage,
          [action.results.page]: action.results,
        },
        isSearching: false,
      };
    }

    case 'SEARCH_CLEAR_RESULTS': {
      return { ...state, resultsByPage: {}, resultsForSearchId: null };
    }

    case 'SEARCH_AUTOSAVE_DISABLE': {
      return { ...state, autosaveDisabled: true };
    }

    case 'SEARCH_UNINITIALIZE': {
      return {
        ...state,
        initialSearchStarted: false,
        initialized: false,
        landingPageInitialized: false,
      };
    }

    case 'UPDATE_SEARCH_LOCATION':
      return {
        ...state,
        initialSearchLocationSet: true,
        searchLocation: {
          ...action.location,
          hasGeo: !isLocationEmpty(action.location),
        },
      };

    case 'CLEAR_SEARCH_LOCATION':
      return { ...state, searchLocation: EMPTY_SEARCH_LOCATION };

    default:
      return state;
  }
} // Utility operations on state

export function resultsFn(state: MainStoreMainSearchState) {
  return state.resultsByPage[state.pageIndex.toString()];
}

// Helpers

function extractLocationProperties(location: SearchLocation) {
  return {
    location_geo_type: location.geoType,
    location_latitude: location.latitude,
    location_longitude: location.longitude,
    location_state: location.stateCode,
    location_text: location.text,
  };
}

function extractFilters(filters: SearchFiltersWithType<ListingType>) {
  const result = {};
  Object.keys(filters).forEach((filterKey) => {
    let value = filters[filterKey];

    if (
      MAIN_SEARCH_FACETS[filterKey] &&
      value === MAIN_SEARCH_FACETS[filterKey].options
    ) {
      value = 'ALL';
    }

    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    result[`filter_${camelCaseToSnakeCase(filterKey)}`] = value;
  });
  return result;
}

export function getMainSearchTrackingData(
  {
    mainSearch: {
      initialized,
      filters,
      query,
      pageIndex,
      resultsByPage,
      searchLocation,
    },
    searchRadius: { value: radius },
  }: {
    mainSearch: MainStoreMainSearchState;
    searchRadius: MainStoreSearchRadiusState;
  },
  searchFrom?: string,
) {
  if (initialized && searchLocation) {
    const results = resultsByPage[pageIndex.toString()] || {
      nbHits: 0,
      nbPages: 0,
    };
    return {
      current_page: pageIndex + 1,
      geo_location_enabled: Boolean(searchLocation.latitude),
      ...extractFilters(filters),
      search_query: query,
      search_radius: radius,
      number_of_results: results.nbHits,
      number_of_pages: results.nbPages,
      search_from: searchFrom,
      ...extractLocationProperties(searchLocation),
    };
  }

  return {};
}

// Actions
export const updateSearchLocation =
  (location: SearchLocation, didUserChange: boolean) =>
  (dispatch: Dispatch<MainStoreMainSearchAction>) => {
    if (didUserChange) {
      trackEvent(
        'Changed Search Location',
        extractLocationProperties(location),
      );
    }

    return Promise.resolve()
      .then(() => setItem('searchLocation', location))
      .then(() =>
        dispatch({
          type: 'UPDATE_SEARCH_LOCATION',
          location,
        }),
      );
  };
const EVERYWHERE_LOCATION = {
  full: null,
  text: null,
  geoType: null,
  stateCode: null,
  countryCode: null,
  latitude: null,
  longitude: null,
  boundingBoxN: null,
  boundingBoxS: null,
  boundingBoxW: null,
  boundingBoxE: null,
  hasGeo: false,
};
export const clearSearchLocation =
  (didUserChange: boolean) =>
  (dispatch: Dispatch<MainStoreMainSearchAction>) => {
    if (didUserChange) {
      trackEvent('Cleared Search Location');
    }

    return Promise.resolve()
      .then(() => setItem('searchLocation', EVERYWHERE_LOCATION))
      .then(() =>
        dispatch({
          type: 'CLEAR_SEARCH_LOCATION',
        }),
      );
  };

const searchResultsSuccessful = ({
  results,
}: {
  results: MainSearchResults;
}): SuccessAction => ({
  type: 'SEARCH_RESULTS_SUCCESS',
  results,
});

const fetchPageSuccessful = ({
  results,
}: {
  results: MainSearchResults;
}): FetchPageSuccessAction => ({
  type: 'SEARCH_FETCH_PAGE_SUCCESS',
  results,
});

const loadDynamicFacetOptions =
  (queryParams: Record<string, string>) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => { mainSearch: MainStoreMainSearchState },
  ) => {
    const { dynamicFacets, facetsThatArentLoaded } = getState().mainSearch;
    return Promise.all(
      ENDPOINTS.map(({ facetUrl, facetName, facetKey, responseToOptions }) => {
        if (
          (facetsThatArentLoaded.length > 0 &&
            !facetsThatArentLoaded.includes(facetKey)) ||
          (facetsThatArentLoaded.length === 0 &&
            !Object.keys(dynamicFacets).includes(facetKey)) ||
          !queryParams[facetName]
        ) {
          dispatch({
            type: 'SET_SEARCH_FACET_OPTIONS',
            facetKey,
            options: [],
            optionTitles: {},
            optionRankings: {},
          });
          return Promise.resolve();
        }
        return getJson<{ familyId: string; jobSynonym: SalariesJobSynonym }>(
          typeof facetUrl === 'string'
            ? facetUrl
            : facetUrl(queryParams[facetName]),
        ).then((response) => {
          const options = responseToOptions(response);

          dispatch({
            type: 'SET_SEARCH_FACET_OPTIONS',
            facetKey,
            options: options.map((f) => f.id),
            optionTitles: options.reduce(
              (sum, { id, text }) => ({ ...sum, [id]: text }),
              {},
            ),
            optionRankings: options.reduce(
              (sum, { id, usageRanking }) => ({ ...sum, [id]: usageRanking }),
              {},
            ),
          });
        });
      }),
    );
  };

export const setQueryKeywords =
  (keywords: Array<string>) =>
  (dispatch: Dispatch<MainStoreMainSearchAction>) =>
    dispatch({
      type: 'SET_QUERY_KEYWORDS',
      keywords,
    });

export const setSearchFilter =
  (filterKey: string, value: unknown | null | undefined) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    if (filterKey === 'type') {
      invariant(value, 'type can never be set to undefined/null');
    }

    if (value === 'BLOG' && filterKey === 'type') return;

    dispatch({
      type: 'SEARCH_SET_FILTER',
      key: filterKey,
      value,
    });

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setQueryKeywords([]));

    trackEvent('Changed Search Filter', {
      ...getMainSearchTrackingData(getState()),
      filter_name: filterKey,
      filter_value: value,
    });
  };

export const setSearchFilters =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (filters: any) => (dispatch: Dispatch<MainStoreMainSearchAction>) => {
    dispatch({
      type: 'SEARCH_SET_FILTERS',
      filters,
    });
  };

export const setSearchQuery =
  (query: string, didUserChange: boolean) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    const { keywords: stateKeywords } = getState().mainSearch;

    if (didUserChange) {
      trackEvent('Changed Search Query', {
        ...getMainSearchTrackingData(getState()),
        new_query: query,
      });
    }

    const [keywords, parsedQuery] = extractKeywordsFromQuery(
      query,
      REMOTE_KEYWORDS[CURRENT_LOCALE],
    );

    if (Array.isArray(keywords) && keywords.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(
        setSearchFilter('locationTypeFacet', ['REMOTE', 'REMOTE_TEMPORARY']),
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(setQueryKeywords(keywords));
    } else if (Array.isArray(stateKeywords) && stateKeywords.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(setQueryKeywords([]));
    }

    dispatch({
      type: 'SEARCH_SET_QUERY',
      query: parsedQuery,
    });
  };

export function initializeSearch(action: Omit<InitializeAction, 'type'>) {
  return (dispatch: Dispatch<MainStoreMainSearchAction>) => {
    const promises = [
      dispatch({
        type: 'SEARCH_INITIALIZE',
        ...action,
      }),
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(loadDynamicFacetOptions(action.queryParams)),
    ];

    if (action.location) {
      promises.push(
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dispatch<any>(updateSearchLocation(action.location, false)),
      );
    }

    return Promise.all(promises);
  };
}

export const uninitializeSearch =
  () => (dispatch: Dispatch<MainStoreMainSearchAction>) =>
    dispatch({
      type: 'SEARCH_UNINITIALIZE',
    });

export const initializeLandingPageSearch =
  ({
    location,
    searchRadius,
    landingPagePath,
    filters,
    autosaveSearch,
    query,
  }: {
    location?: SearchLocation;
    searchRadius?: string;
    landingPagePath?: string;
    filters?: SearchFilters;
    autosaveSearch?: boolean;
    query?: string | null | undefined;
  } = {}) =>
  (dispatch: Dispatch<MainStoreMainSearchAction>) => {
    const initializeSearchAction: InitializeLandingPageAction = {
      type: 'SEARCH_INITIALIZE_LANDING_PAGE',
      landingPagePath,
      filters,
      autosaveSearch,
    };

    if (searchRadius) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(
        setSearchRadiusBase(
          searchRadius,
          false,
          /* userHasChanged */
        ),
      );
    }

    if (query) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(setSearchQuery(query, false));
    }

    if (location) {
      return Promise.all([
        dispatch(initializeSearchAction),
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dispatch<any>(loadDynamicFacetOptions({})),
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dispatch<any>(updateSearchLocation(location, false)),
      ]);
    }

    return Promise.all([
      dispatch(initializeSearchAction),
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(loadDynamicFacetOptions({})),
    ]);
  };
export const searchResetPage = (): ResetPageAction => ({
  type: 'SEARCH_RESET_PAGE',
});
export const setSearchRadius =
  (radius: string, didUserChange: boolean) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    if (didUserChange) {
      trackEvent('Changed Search Radius', {
        ...getMainSearchTrackingData(getState()),
        radius,
      });
    }

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setSearchRadiusBase(radius, didUserChange));
    dispatch(searchResetPage());
  };
export const setSearchSort =
  (sortParam: SortType | GradSortType) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    const state = getState();
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line prefer-destructuring
    const searchRadius = state.searchRadius;
    trackEvent('Changed Search Sort', {
      ...getMainSearchTrackingData(state),
      sort_order: sortParam,
    });
    dispatch({
      type: 'SEARCH_SET_SORT',
      sortParam,
    });

    if (!searchRadius.userHasChanged) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(setSearchRadius(getDefaultSearchRadius(sortParam), false));
    }

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setQueryKeywords([]));
  };
export const setPageIndex =
  (pageIndex: number, didUserChange: boolean) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    if (didUserChange) {
      trackEvent('Changed Search Page Number', {
        ...getMainSearchTrackingData(getState()),
        new_page: pageIndex,
      });
    }

    dispatch({
      type: 'SEARCH_SET_PAGE_INDEX',
      pageIndex,
    });
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setQueryKeywords([]));
  };

export const resetSearchFilters = (): ResetFiltersAction => ({
  type: 'SEARCH_RESET_FILTERS',
});

export const clearAllFilters =
  () =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => { mainSearch: MainStoreMainSearchState },
  ) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line prefer-destructuring
    const type = getState().mainSearch.filters.type;
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const sort = getState().mainSearch.sort[type];
    batch(() => {
      dispatch(resetSearchFilters());
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(setSearchRadius(getDefaultSearchRadius(sort), false));
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(clearSearchLocation(true));
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch<any>(setQueryKeywords([]));
    });
  };

export const selectSearchFilterOption =
  (filterKey: string, option: string | null | undefined) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    const selectedOptions = (state: {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    }) => [...(state.mainSearch.filters[filterKey] || []), option];

    dispatch({
      type: 'SEARCH_SELECT_FILTER_OPTION',
      key: filterKey,
      option,
    });

    const state = getState();
    trackEvent('Changed Search Filter', {
      ...getMainSearchTrackingData(state),
      filter_name: filterKey,
      filter_value: selectedOptions(state),
    });
  };

export const deselectSearchFilterOption =
  (filterKey: string, option: string | null | undefined) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    const selectedOptions = (state: {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    }) => {
      const options = state.mainSearch.filters[filterKey];
      invariant(Array.isArray(options), 'Invalid options type');
      return options.filter((v) => v !== option);
    };

    dispatch({
      type: 'SEARCH_DESELECT_FILTER_OPTION',
      key: filterKey,
      option,
    });
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setQueryKeywords([]));

    const state = getState();
    trackEvent('Changed Search Filter', {
      ...getMainSearchTrackingData(state),
      filter_name: filterKey,
      filter_value: selectedOptions(state),
    });
  };

export const selectSearchFilterSpecificOptions =
  (filterKey: string, options: Array<string>) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    dispatch({
      type: 'SEARCH_SELECT_FILTER_SPECIFIC_OPTIONS',
      key: filterKey,
      options,
    });
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setQueryKeywords([]));

    trackEvent('Changed Search Filter', {
      ...getMainSearchTrackingData(getState()),
      filter_name: filterKey,
      filter_value: options,
    });
  };

export const selectAllSearchFilterOptions =
  (filterKey: string) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    dispatch({
      type: 'SEARCH_SELECT_ALL_FILTER_OPTIONS',
      key: filterKey,
    });
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(setQueryKeywords([]));

    trackEvent('Changed Search Filter', {
      ...getMainSearchTrackingData(getState()),
      filter_name: filterKey,
      filter_value: 'ALL',
    });
  };

export const setUserInitiatedSearch = (searchMode = true) => ({
  type: 'SEARCH_INITIATED_BY_USER',
  searchMode,
});

export function mainStoreSearchFunction() {
  return (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    dispatch({
      type: 'SEARCH_STARTED',
    });
    const state = getState();
    const { filters, searchId, resultsForSearchId } = state.mainSearch;
    const newSearch = searchId !== resultsForSearchId;

    if (newSearch && filters.type !== 'ALL' && filters.type !== 'ORG') {
      return executeSearchQuery({
        state,
      }).then((results) => {
        dispatch(searchResultsSuccessful({ results }));
        return results;
      });
    }

    return executeSearchQuery({ state }).then((results) => {
      if (newSearch) {
        dispatch(searchResultsSuccessful({ results }));
      } else {
        dispatch(searchResultsSuccessful({ results }));
      }

      return results;
    });
  };
}

export const fetchPage =
  (pageIndex: number) =>
  (
    dispatch: Dispatch<MainStoreMainSearchAction>,
    getState: () => {
      mainSearch: MainStoreMainSearchState;
      searchRadius: MainStoreSearchRadiusState;
    },
  ) => {
    dispatch({
      type: 'SEARCH_STARTED',
    });
    executeSearchQuery({ state: getState(), pageIndex }).then((results) => {
      dispatch(fetchPageSuccessful({ results }));
    });
  };

export const clearResults = (): ClearResultsAction => ({
  type: 'SEARCH_CLEAR_RESULTS',
});

export const searchAutosaveDisable =
  () => (dispatch: Dispatch<MainStoreMainSearchAction>) => {
    dispatch({
      type: 'SEARCH_AUTOSAVE_DISABLE',
    });
  };
